import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import apiClient from '../utils/api';
import DOMPurify from 'dompurify';

/**
 * Generic renderer for the public /features/* landing pages.
 *
 * Content is authored once in the backend (services/feature_pages.py) and
 * fetched here by slug, so the copy is server-rendered into the HTML shell for
 * crawlers AND rendered live for JS users from the same source. The body uses
 * the shared visual classes from src/index.css (feature-grid, cta-primary, ...);
 * those divs already carry `not-prose` so `prose` typography doesn't override
 * their layout.
 */
const FeaturePage = ({ slug }) => {
  const [page, setPage] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setPage(null);
    setError('');
    apiClient
      .get(`/api/pages/${slug}`)
      .then((res) => { if (active) setPage(res.data); })
      .catch(() => { if (active) setError('Page not found.'); });
    return () => { active = false; };
  }, [slug]);

  // Keep the tab title + meta description correct during client-side navigation
  // (the server already injects them into the initial HTML for crawlers).
  useEffect(() => {
    if (page?.title) document.title = page.title;
    return () => { document.title = 'Vegetablah'; };
  }, [page?.title]);

  useEffect(() => {
    if (page?.description) {
      let meta = document.querySelector('meta[name="description"]');
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = 'description';
        document.head.appendChild(meta);
      }
      meta.content = page.description;
    }
  }, [page?.description]);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {!page && !error && (
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400">Loading…</p>
        </div>
      )}

      {error && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <p className="text-off mb-4">{error}</p>
              <Link to="/"><Button>Back home</Button></Link>
            </div>
          </CardContent>
        </Card>
      )}

      {page && (
        <Card className="border-0">
          <CardContent className="py-8">
            <div
              className="prose prose-lg dark:prose-invert max-w-none prose-headings:text-gray-900 dark:prose-headings:text-white prose-p:text-gray-700 dark:prose-p:text-gray-300"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(page.html, { ADD_ATTR: ['target', 'rel'] }),
              }}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FeaturePage;
